"use client";

import { useEffect, useMemo, useState } from "react";
import debounce from "lodash.debounce";
import { Search, X, ArrowLeft } from "lucide-react";
import "./UniversalSearchInput.css";

type Props = {
  placeholder?: string;
  className?: string;
  initialValue?: string;
  position?: "left" | "right";
  onSearch: (query: string) => void;
  renderResults?: (query: string) => React.ReactNode;
};

const UniversalSearchInput = ({
  placeholder = "Search...",
  className = "",
  initialValue = "",
  position = "right",
  onSearch,
  renderResults,
}: Props) => {
  const [search, setSearch] = useState(initialValue);
  const [isOpen, setIsOpen] = useState(false);

  const debouncedSearch = useMemo(
    () =>
      debounce((term: string) => {
        onSearch(term.trim());
      }, 500),
    [onSearch]
  );

  useEffect(() => {
    if (isOpen) debouncedSearch(search);
    return () => debouncedSearch.cancel();
  }, [search, debouncedSearch, isOpen]);

  const handleClear = () => setSearch("");

  return (

    <div className={`search-wrapper ${className} position-${position}`}>
      <button onClick={() => setIsOpen(!isOpen)} className="icon-button">
        {isOpen ? <ArrowLeft size={24} /> : <Search size={26} strokeWidth={1.4} />}
      </button>

      {isOpen && (
        <div className="search-box-wrapper">
          <div className={`search-bar animated open-from-${position}`}>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={placeholder}
              className="search-input"
              autoFocus
            />

            {search && (
              <button onClick={handleClear} className="icon-button">
                <span>Clear</span>
              </button>
            )}

            <button onClick={() => setIsOpen(false)} className="icon-button close-button">
              <X size={20} />
            </button>
          </div>

          {renderResults && (
            <div
              className="search-results"
              style={position === "left" ? { left: 0 } : { right: 0 }}
            >
              {renderResults(search)}
            </div>
          )}
        </div>
      )}

    </div>

  );
};

export default UniversalSearchInput;
